/**
 * Подписка на своё сито: что человек хочет получать в Telegram.
 *
 * Подписка — не «включить оповещения». Включённые оповещения на всю ленту
 * умирают за неделю: человек получает сорок сообщений в час, привыкает их не
 * читать и выключает канал целиком. Подписка на конкретный фильтр — это
 * обещание другого рода: приходит то, что человек взял бы, и потому каждое
 * сообщение стоит того, чтобы посмотреть.
 *
 * Сито не заводится отдельно от фильтров ленты, и это главное решение здесь.
 * Второе место, где перечислены «мои конторы», разъедется с первым за месяц:
 * человек порежется в конторе, поправит фишки над лентой и не вспомнит, что
 * где-то в настройках оповещений лежит её копия. Подписываются ровно на то, что
 * сейчас видно на экране, — тем и объясняется кнопка внутри сита, а не в
 * отдельном разделе.
 *
 * Кнопки нет там, где сообщение не дойдёт. На контуре тестеров человек назван
 * паролем в двери Caddy, бот про него не знает, и подписка молча не сработала бы
 * — сервер отвечает `canSend: false`, и мы просто не предлагаем.
 */

import { create } from 'zustand';

import { apiUrl } from '../lib/build';
import type { Filters } from './filters';
import { parked } from './parked';

/** Сито, каким оно уезжает на сервер. Имена полей общие с `/v1/profile`. */
export interface WatchWire {
  name: string;
  venues: string[];
  venueMode: 'only' | 'except' | 'main';
  sports: string[];
  lines: string[];
  roiMin: string | null;
  roiMax: string | null;
}

/** Собрать подписку из текущих фильтров ленты. */
export function fromFilters(f: Filters, name: string): WatchWire {
  return {
    name,
    venues: f.venues,
    venueMode: f.venueMode,
    sports: f.sports,
    lines: f.lines,
    // Границы строками: на сервере процент — Decimal, и число с плавающей
    // точкой теряло бы сотую там, где по ней принимают решение.
    roiMin: f.roiMin === null ? null : String(f.roiMin),
    roiMax: f.roiMax === null ? null : String(f.roiMax),
  };
}

/** Описать подписку словами — то, что человек читает вместо списка полей. */
export function watchText(watch: WatchWire): string {
  const parts: string[] = [];
  if (watch.venues.length > 0) {
    const how = watch.venueMode === 'only' ? 'только' : watch.venueMode === 'main' ? 'главная' : 'кроме';
    parts.push(`${how} ${watch.venues.length} контор`);
  }
  if (watch.sports.length > 0) parts.push(`${watch.sports.length} дисц.`);
  if (watch.lines.length > 0) {
    parts.push(watch.lines.map((one) => (one === 'live' ? 'лайв' : 'прематч')).join('+'));
  }
  if (watch.roiMin !== null || watch.roiMax !== null) {
    parts.push(`процент ${watch.roiMin ?? '…'}–${watch.roiMax ?? '…'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'вся лента';
}

/** Совпадает ли подписка с тем, что стоит на ленте сейчас. */
export function same(watch: WatchWire, now: WatchWire): boolean {
  const alike = (a: string[], b: string[]): boolean =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join();
  return (
    alike(watch.venues, now.venues) &&
    // Режим списка значим только тогда, когда список непустой: «кроме ничего» и
    // «только ничего» — это одно и то же состояние, и разводить их означало бы
    // предлагать обновить подписку, в которой ничего не меняется.
    (watch.venues.length === 0 || watch.venueMode === now.venueMode) &&
    alike(watch.sports, now.sports) &&
    alike(watch.lines, now.lines) &&
    watch.roiMin === now.roiMin &&
    watch.roiMax === now.roiMax
  );
}

interface WatchState {
  /** Дойдёт ли сообщение вообще. `null` — ещё не спрашивали. */
  canSend: boolean | null;
  /** На что подписан сейчас. `null` — не подписан. */
  watch: WatchWire | null;
  busy: boolean;
  load: () => Promise<void>;
  save: (watch: WatchWire) => Promise<void>;
  drop: () => Promise<void>;
}

const wire = (given: unknown): WatchWire | null => {
  if (typeof given !== 'object' || given === null) return null;
  const one = given as Record<string, unknown>;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  const edge = (value: unknown): string | null => (typeof value === 'string' ? value : null);
  return {
    name: typeof one.name === 'string' ? one.name : '',
    venues: list(one.venues),
    venueMode: one.venueMode === 'only' || one.venueMode === 'main' ? one.venueMode : 'except',
    sports: list(one.sports),
    lines: list(one.lines),
    roiMin: edge(one.roiMin),
    roiMax: edge(one.roiMax),
  };
};

async function put(body: unknown): Promise<Response | null> {
  try {
    return await fetch(apiUrl('/v1/profile'), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return null;
  }
}

export const useWatch = create<WatchState>((set, get) => ({
  canSend: null,
  watch: null,
  busy: false,

  load: async () => {
    if (parked.watchTg) {
      set({ canSend: false });
      return;
    }
    try {
      const res = await fetch(apiUrl('/v1/profile'), { credentials: 'same-origin' });
      if (!res.ok) {
        set({ canSend: false });
        return;
      }
      const body = (await res.json()) as {
        profile?: { watch?: unknown };
        alerts?: { canSend?: boolean };
      };
      set({
        canSend: body.alerts?.canSend === true,
        watch: wire(body.profile?.watch),
      });
    } catch {
      // Оповещения — не лента. Молчим и не показываем кнопку.
      set({ canSend: false });
    }
  },

  save: async (watch) => {
    if (parked.watchTg || get().busy) return;
    set({ busy: true });
    const res = await put({ profile: { watch } });
    // Ставим то, что подтвердил сервер: он вправе выбросить негодное поле, и
    // показывать человеку подписку, которой у него нет, нельзя.
    if (res !== null && res.ok) {
      const body = (await res.json().catch(() => null)) as { profile?: { watch?: unknown } } | null;
      set({ watch: wire(body?.profile?.watch) ?? watch });
    }
    set({ busy: false });
  },

  drop: async () => {
    if (parked.watchTg || get().busy) return;
    set({ busy: true });
    const res = await put({ profile: { watch: null } });
    if (res !== null && res.ok) set({ watch: null });
    set({ busy: false });
  },
}));
